/**************
 New Cart Drawer — namespaced (#ncdCart / .ncd)
 Self-contained; does not touch the legacy #miniCart drawer.
 ***************/
(function () {
  'use strict';

  const dialog = document.getElementById('ncdCart');
  if (!dialog) return;

  /* ── QA affordance: preview a variant without devtools ──────────────
     The class-gated test (ig-i7) is switched on by Intelligems' variant JS,
     which only runs against the LIVE theme. On a preview theme no test targets you, so
     the drawer always renders control — which reads as "the feature is missing" when
     you're actually looking at the baseline.

     ?ig=i7 adds that class so a variant can be QA'd by URL. Comma-separated ids work
     too (?ig=i7,i9) for whatever is running next — unknown ids are simply inert.

     Hard-limited to non-published themes: on the live theme this does nothing, so a
     shared or crawled link can never show a visitor a variant Intelligems is counting
     as control. Fails closed when the role is unknown. */
  try {
    const igParam = new URLSearchParams(location.search).get('ig');
    const themeRole = (window.Shopify && window.Shopify.theme && window.Shopify.theme.role) || '';
    if (igParam && themeRole && themeRole !== 'main') {
      igParam.split(',').forEach(function (t) {
        const id = t.trim().toLowerCase();
        if (/^i[0-9]{1,2}$/.test(id)) document.documentElement.classList.add('ig-' + id);
      });
    }
  } catch (e) { /* never let a QA helper break the cart */ }

  const THRESHOLD = parseInt(dialog.dataset.freeShippingThreshold, 10) || 0;
  const MSG_UNLOCKED = dialog.dataset.msgUnlocked || "You've Unlocked Free Shipping!";
  const MSG_REMAINING = dialog.dataset.msgRemaining || "Spend [amount] to unlock Free Shipping";
  /* Carts that can't reach free shipping by spending (see updateProgress). Two strings
     because the honest next step differs: a monthly subscriber upgrades their plan, a
     one-time buyer has to subscribe first. The subscriber line ships to everyone; the
     one-time line is still gated on ig-i7. */
  const MSG_UPSELL_SUB = dialog.dataset.msgUpsellSub || '';
  const MSG_UPSELL_OTP = dialog.dataset.msgUpsellOtp || '';
  const freeGifts = window.ncdFreeGifts || [];
  const hiddenSKUs = window.ncdHiddenSKUs || [];
  const referencePriceVariants = (window.ncdReferencePriceVariants || []).map(Number);
  /* If non-empty, free shipping is unlocked by having one of these products in the
     cart instead of by hitting the spend threshold. Empty = default milestone mode. */
  const freeShipProducts = window.ncdFreeShipProducts || [];
  /* Extra unlock rules (OR'd with the above): any subscription longer than 1 month,
     and — for the selected products — only count them when they're subscribed. */
  const FREE_SHIP_MULTIMONTH = dialog.dataset.freeShipMultimonth === 'true';
  const FREE_SHIP_SELECTED_REQ_SUB = dialog.dataset.freeShipSelectedSub === 'true';

  /* Mirror of the Checkout Blocks "Free Shipping bundle" function's SKU list (see the
     section template). Normalised once so line SKUs can be compared without worrying
     about stray whitespace or casing when someone re-pastes the list. When it is
     non-empty it is authoritative and every other free-shipping rule is skipped. */
  const freeShipSkus = (window.ncdFreeShipSkus || [])
    .map(function (s) { return String(s || '').trim().toLowerCase(); })
    .filter(Boolean);
  function skuQualifies(sku) {
    if (!freeShipSkus.length || !sku) return false;
    return freeShipSkus.indexOf(String(sku).trim().toLowerCase()) !== -1;
  }
  /* "Would this ladder rung earn free shipping if the buyer took it?" — the question the
     upsell strip has to answer before it promises anything. One place, so the offer and
     the promise can't drift apart. Falls back to the plan-interval test only while the
     SKU mirror is empty (see freeShippingUnlocked). */
  function tierEarnsFreeShipping(sku, planName) {
    if (freeShipSkus.length) return skuQualifies(sku);
    return FREE_SHIP_MULTIMONTH && subIsMultiMonth(planName);
  }

  /* Free-gift value (from the "Gift value" cart setting, e.g. "$10") in cents — added to
     the crossed-out subtotal + savings so the gifts count toward the displayed discount. */
  const GIFT_VALUE_CENTS = Math.round((parseFloat(String(dialog.dataset.giftValue || '').replace(/[^0-9.]/g, '')) || 0) * 100);

  /* Shipping value (from the "Shipping value" cart setting, e.g. "$4.99") in cents — added once
     to the crossed-out subtotal + savings so free shipping counts toward the displayed discount.
     Blank setting → 0 → no effect. */
  const SHIP_VALUE_CENTS = Math.round((parseFloat(String(dialog.dataset.shippingValue || '').replace(/[^0-9.]/g, '')) || 0) * 100);

  /* Subscribe & Save upsell box (copy from section settings) */
  const subBoxCfg = window.ncdSubBox || {};
  const subBoxPerks = String(subBoxCfg.perksRaw || '').split('\n').map(s => s.trim()).filter(Boolean);
  const productCache = {};
  const inflightUpsell = {};   // handle -> in-flight ladder request, so overlapping renders share one

  let cart = null;
  let isUpdating = false;
  /* Upsell strip copy chosen by updateProgress but only painted by injectSubBoxes,
     once the offered tier is confirmed to earn free shipping. See updateProgress.
     pendingUpsellHasSub travels with it because the honesty test needs to know whether
     the cart was already subscribed — injectSubBoxes can't recover that from `alloc`. */
  let pendingUpsellMsg = '';
  let pendingUpsellHasSub = false;
  /* Set once a shopper accepts an upgrade, and cleared when the drawer closes. The ladder
     has three rungs, so without this the slot that just said "Upgrade to 3 Bottles" comes
     straight back as "Upgrade to 6 Bottles" in the same position, in the same frame the
     upgrade lands — asking again before acknowledging the yes. It arrives at the worst
     possible moment: immediately after a commitment, one tap from checkout, which is
     exactly where second-guessing costs the order rather than the upsell.
     Scoped to the drawer session, not the cart: reopening offers the next rung again, so
     nothing is lost from someone who genuinely wants to keep climbing. */
  let upsellTakenThisOpen = false;
  /* Resolves when the in-flight free-gift reconciliation has settled. The drawer becomes
     visible (and its Checkout link becomes tappable) BEFORE the gift POST returns, so a
     fast tap could reach checkout with the gifts missing. The checkout handler waits on
     this — briefly, and never indefinitely. */
  let giftsSettled = Promise.resolve();
  let detailsOpen = false;
  let renderToken = 0;

  const els = {
    count: document.getElementById('ncdCount'),
    progress: document.getElementById('ncdProgress'),
    progressText: document.getElementById('ncdProgressText'),
    progressFill: document.getElementById('ncdProgressFill'),
    progressGoal: document.getElementById('ncdProgressGoal'),
    items: document.getElementById('ncdItems'),
    gifts: document.getElementById('ncdGifts'),
    giftsToggle: document.getElementById('ncdGiftsToggle'),
    giftsList: document.getElementById('ncdGiftsList'),
    giftsCount: document.getElementById('ncdGiftsCount'),
    empty: document.getElementById('ncdEmpty'),
    footer: document.getElementById('ncdFooter'),
    total: document.getElementById('ncdTotal'),
    savePct: document.getElementById('ncdSavePct'),
    subtotal: document.getElementById('ncdSubtotal'),
    bundleSavings: document.getElementById('ncdBundleSavings'),
    discountCode: document.getElementById('ncdDiscountCode'),
    discountCodeSavings: document.getElementById('ncdDiscountCodeSavings'),
    savingsBreakdown: document.getElementById('ncdSavingsBreakdown'),
    savings: document.getElementById('ncdSavings'),
    itemsCount: document.getElementById('ncdItemsCount'),
    giftsValue: document.getElementById('ncdGiftsValue'),
    shipValue: document.getElementById('ncdShipValue'),
    toggleDetails: document.getElementById('ncdToggleDetails'),
    checkout: dialog.querySelector('.ncd__checkout'),
    recs: document.getElementById('ncdRecs'),
    recsTrack: document.getElementById('ncdRecsTrack'),
  };

  const hasGiftsConfigured = els.gifts && els.gifts.querySelectorAll('.ncd__gift').length > 0;
  const hasRecs = els.recs && els.recs.querySelectorAll('.ncd__rec').length > 0;

  function money(cents) { return '$' + (cents / 100).toFixed(2); }

  /* Cart-level automatic discounts can share this array with customer-entered or
     link-applied codes. Only discount_code entries belong in the labeled code row. */
  function cartLevelCodeApplications(c) {
    return (c && c.cart_level_discount_applications ? c.cart_level_discount_applications : []).filter(function (app) {
      const code = String((app && (app.code || app.title)) || '').trim();
      return app && app.type === 'discount_code' && code && (Number(app.total_allocated_amount) || 0) > 0;
    });
  }

  function cartSig(c) {
    if (!c || !c.items) return '0';
    const cartDiscounts = (c.cart_level_discount_applications || []).map(function (app) {
      return (app.title || app.code || '') + ':' + (Number(app.total_allocated_amount) || 0);
    }).join(',');
    return c.item_count + '|' + c.items_subtotal_price + '|' + (Number(c.total_price) || 0) + '|' + cartDiscounts + '|' + c.items.map(function (i) {
      return i.key + ':' + i.quantity + ':' + (Number(i.final_line_price) || 0) + ':' +
        ((i.selling_plan_allocation && i.selling_plan_allocation.selling_plan && i.selling_plan_allocation.selling_plan.id) || '');
    }).join(',');
  }

  function billingText(name) {
    if (!name) return '';
    let n = name.replace(/^Deliver(y)?\s+/i, '').trim();
    if (!/^every/i.test(n)) n = 'every ' + n;
    return 'Billed ' + n.toLowerCase();
  }

  /* Shopify keeps order-class discounts (including codes applied by /discount/ links)
     at cart level instead of folding them into item.final_line_price. Build one effective
     price per visible line so the item row and footer agree with checkout. Product-class
     discounts are already included in final_line_price; cart-level allocations are applied
     afterwards, matching Shopify's calculation order. */
  function pricedVisibleItems(c) {
    const items = (c && c.items ? c.items : []).filter(function (item) {
      return !hiddenSKUs.includes(item.product_id) && !freeGifts.includes(item.variant_id);
    });
    const lineSubtotal = items.reduce(function (sum, item) {
      return sum + (Number(item.final_line_price) || 0);
    }, 0);
    const requestedCartDiscount = (c.cart_level_discount_applications || []).reduce(function (sum, app) {
      return sum + (Number(app.total_allocated_amount) || 0);
    }, 0);
    const cartDiscount = Math.min(lineSubtotal, Math.max(0, requestedCartDiscount));
    let allocated = 0;

    return items.map(function (item, index) {
      let discountShare = 0;
      if (cartDiscount > 0 && lineSubtotal > 0) {
        discountShare = index === items.length - 1
          ? cartDiscount - allocated
          : Math.round(cartDiscount * item.final_line_price / lineSubtotal);
        discountShare = Math.max(0, Math.min(item.final_line_price, discountShare));
        allocated += discountShare;
      }
      const lineTotal = Math.max(0, item.final_line_price - discountShare);
      const planName = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
        ? item.selling_plan_allocation.selling_plan.name : null;
      return {
        item: item,
        planName: planName,
        lineTotal: lineTotal,
        unit: item.quantity ? lineTotal / item.quantity : lineTotal
      };
    });
  }

  /* ── Open / Close ─────────────────────────────────── */
  async function openCart(skipFetch) {
    if (isUpdating) {
      /* A cart mutation is in flight. Returning outright meant a tap on the cart icon did
         nothing at all — and now that the mutators no longer force the drawer open
         (see refreshIfOpen), nothing would open it afterwards either. Show what we already
         have; the in-flight mutation finishes with a refresh that fills in the rest. */
      if (!dialog.open) {
        if (cart) render(cart);
        dialog.showModal();
        document.body.classList.add('ncd-no-scroll');
      }
      return;
    }
    isUpdating = true;
    try {
      // Open instantly using the cart we already have; only re-render if it actually changed.
      const hadCart = !!cart;
      if (hadCart) render(cart);
      if (!dialog.open) dialog.showModal();
      document.body.classList.add('ncd-no-scroll');

      if (!skipFetch || !cart) {
        const fresh = await fetch('/cart.js').then(r => r.json());
        const changed = !hadCart || cartSig(fresh) !== cartSig(cart);
        cart = fresh;
        if (changed) render(cart);
      }

      const giftWork = handleFreeGifts(cart);
      giftsSettled = giftWork.catch(function () { return null; });
      const updated = await giftWork;
      if (updated) { cart = updated; render(cart); }
    } catch (e) {
      console.error('New cart drawer error:', e);
    } finally {
      isUpdating = false;
    }
  }

  /* How the in-drawer mutators (add-from-recommendation, quantity, remove) re-sync: by
     re-opening the drawer, because openCart is what re-fetches /cart.js and re-renders.
     That is right while the drawer is on screen and wrong the moment it isn't — closing
     the drawer ~0.3s after tapping remove made it reappear ~1.5s later and stay open,
     i.e. the cart shoving itself back in front of someone who had just dismissed it
     (reproduced 2026-07-30). A closed drawer needs no refresh: the next open re-fetches. */
  async function refreshIfOpen() {
    if (dialog.open) await openCart();
  }

  function closeCart() {
    if (dialog.open) dialog.close();
    document.body.classList.remove('ncd-no-scroll');
  }

  /* ── Counts ───────────────────────────────────────── */
  function effectiveCount(c) {
    if (!c || !c.items) return 0;
    return c.items.reduce((n, it) => {
      if (freeGifts.includes(it.variant_id)) return n;
      if (hiddenSKUs.includes(it.product_id)) return n;
      return n + it.quantity;
    }, 0);
  }

  /* ── Render ───────────────────────────────────────── */
  function render(c) {
    renderToken++;
    /* Every render re-decides the strip from scratch. Clearing here and not only in
       updateProgress matters: an emptied cart returns below before updateProgress runs,
       and a value left over from the previous cart would then be painted onto the next
       one — a fresh drawer, untouched by the buyer, promising an upgrade it never offered. */
    pendingUpsellMsg = '';
    pendingUpsellHasSub = false;
    const count = effectiveCount(c);
    document.querySelectorAll('.headerCart').forEach(el => el.setAttribute('count', count));
    if (els.count) els.count.textContent = count;

    els.items.innerHTML = '';

    if (count === 0) {
      setEmptyState();
      return;
    }
    setActiveState();

    /* A listed line whose ladder isn't cached yet: fetch it and re-render once it lands so the
       strike and Savings use the reference price. No loop: a failed fetch resolves to null. */
    const refHandles = c.items
      .filter(it => referencePriceVariants.includes(Number(it.variant_id)) && !productCache[it.handle])
      .map(it => it.handle);
    if (refHandles.length) {
      const token = renderToken;
      Promise.all(refHandles.map(getUpsellData)).then(function (ds) {
        if (token === renderToken && ds.some(Boolean)) render(c);
      });
    }

    pricedVisibleItems(c).forEach(function (priced) {
      const item = priced.item;
      const oUnit = originalUnit(item, priced.planName, priced.unit);
      const compareUnit = oUnit > priced.unit ? oUnit : 0;

      els.items.insertAdjacentHTML(
        'beforeend',
        buildItem(item, priced.planName, compareUnit, priced.lineTotal)
      );
    });

    updateTotals(c);
    attachItemListeners();
    injectSubBoxes(c, renderToken);
  }

  /* The "main"/original unit price for a line — used for the crossed-out price and the
     Subtotal. Priority: the variant's compare-at price (if the cart exposes it and it's
     higher) → the subscription's regular (compare-at) unit → the pre-discount unit → the
     plain final unit when there's no discount at all. Always >= the final unit price. */
  /* Reference unit for a listed variant: its pack size x the product's 1-unit price, read
     from the cached ?view=upsell ladder (the same data the upsell box uses). 0 when the
     line isn't listed or the ladder hasn't loaded; render() fetches it and re-renders once.
     Lets a pack with no compare-at (Lungs 2-pack) show savings vs single bottles without
     changing its compare-at price, which would also move the price on other channels. */
  function referenceUnit(item) {
    if (!referencePriceVariants.includes(Number(item.variant_id))) return 0;
    const d = productCache[item.handle];
    if (!d || !d.variants) return 0;
    const cur = d.variants.find(v => v.id === item.variant_id);
    const single = d.variants.find(v => v.qty === 1 && v.price > 0);
    if (!cur || !single || !(cur.qty > 1)) return 0;
    return single.price * cur.qty;
  }

  function originalUnit(item, planName, effectiveUnit) {
    const finalUnit = effectiveUnit == null ? item.final_line_price / item.quantity : effectiveUnit;
    const refUnit = referenceUnit(item);
    if (refUnit > finalUnit) return refUnit;
    if (item.compare_at_price && item.compare_at_price > finalUnit) return item.compare_at_price;
    if (planName && item.selling_plan_allocation && item.selling_plan_allocation.compare_at_price) {
      return item.selling_plan_allocation.compare_at_price;
    }
    if (item.original_line_price > item.final_line_price) return item.original_line_price / item.quantity;
    return finalUnit;
  }

  /* Cart-level numbers (subtotal/savings/total/count/progress/gifts). Reused for the
     partial refresh after subscribing so the item list (and the upsell box) aren't rebuilt. */
  function updateTotals(c) {
    const count = effectiveCount(c);
    document.querySelectorAll('.headerCart').forEach(el => el.setAttribute('count', count));
    if (els.count) els.count.textContent = count;
    if (els.itemsCount) els.itemsCount.textContent = count;

    let productsRegular = 0; // Subtotal — compare-at / regular price of the purchased lines
    let bundleSavings = 0;   // subscription product savings before any cart-level promo code
    let finalTotal = 0;      // what the customer actually pays after line + order discounts
    pricedVisibleItems(c).forEach(function (priced) {
      const item = priced.item;
      const regularLine = originalUnit(item, priced.planName, priced.unit) * item.quantity;
      finalTotal += priced.lineTotal;
      productsRegular += regularLine;
      /* Order-class codes live in cart_level_discount_applications and are deliberately
         excluded here. Restricting this row to subscription lines also keeps ordinary
         compare-at markdowns from being mislabeled as bundle savings. */
      if (priced.planName) {
        bundleSavings += Math.max(0, regularLine - (Number(item.final_line_price) || 0));
      }
    });
    /* "Free gifts + Shipping" — the value the customer gets for free, shown on its own line
       so the shopper sees every part of the discount at once. Each gift's configured value,
       plus the shipping value ONCE and ONLY when this cart actually qualifies for free
       shipping (same rules as the progress bar) — a 1-bottle monthly sub doesn't get free
       shipping, so its savings must not include the $4.99. Both stay $0 in finalTotal, so
       the Total the customer actually pays is unchanged — only the displayed discount grows. */
    const giftUnits = c.items.reduce((n, it) => freeGifts.includes(it.variant_id) ? n + it.quantity : n, 0);
    const giftValue = giftUnits * GIFT_VALUE_CENTS;
    const shipValue = (finalTotal > 0 && freeShippingUnlocked(c)) ? SHIP_VALUE_CENTS : 0;
    const giftsShipping = giftValue + shipValue;
    const originalTotal = productsRegular + giftsShipping;
    const savings = originalTotal - finalTotal;
    const codeApplications = cartLevelCodeApplications(c);
    const codeSavings = codeApplications.reduce(function (sum, app) {
      return sum + (Number(app.total_allocated_amount) || 0);
    }, 0);
    const codeNames = codeApplications.map(function (app) {
      return String(app.code || app.title || '').trim();
    }).filter(function (code, index, codes) {
      return code && codes.indexOf(code) === index;
    });

    if (els.total) els.total.textContent = money(finalTotal);          // Total    = final price paid
    if (els.subtotal) {                                                // Subtotal = products' regular total
      els.subtotal.textContent = money(productsRegular);
      els.subtotal.classList.toggle('is-struck', savings > 0);         // strike through when Total < Subtotal
    }
    if (els.bundleSavings) {                                           // Subscription bundle discount before order codes
      els.bundleSavings.textContent = money(bundleSavings);
      const bundleRow = els.bundleSavings.closest('.ncd__detail-row');
      if (bundleRow) bundleRow.style.display = bundleSavings > 0 ? '' : 'none';
    }
    if (els.discountCode && els.discountCodeSavings) {                 // Applied order code — dynamic name and allocation
      els.discountCode.textContent = codeNames.join(', ');
      els.discountCodeSavings.textContent = money(codeSavings);
      const discountRow = els.discountCodeSavings.closest('.ncd__detail-row');
      if (discountRow) discountRow.style.display = codeNames.length && codeSavings > 0 ? '' : 'none';
    }
    if (els.giftsValue) {                                              // Free gifts row — only when gifts are in the cart
      els.giftsValue.textContent = money(giftValue);
      const giftsRow = els.giftsValue.closest('.ncd__detail-row');
      if (giftsRow) giftsRow.style.display = giftValue > 0 ? '' : 'none';
    }
    if (els.shipValue) {                                               // Free shipping row — only when shipping is actually free
      els.shipValue.textContent = money(shipValue);
      const shipRow = els.shipValue.closest('.ncd__detail-row');
      if (shipRow) shipRow.style.display = shipValue > 0 ? '' : 'none';
    }
    if (els.savings) {                                                 // Savings  = Subtotal + Gifts/Shipping − Total
      els.savings.textContent = money(Math.max(0, savings));
      const savingsRow = els.savings.closest('.ncd__detail-row');
      if (savingsRow) savingsRow.style.display = savings > 0 ? '' : 'none';
    }
    if (els.savingsBreakdown) {
      els.savingsBreakdown.hidden = savings <= 0;
    }
    if (els.savePct) {                                                 // pill next to the Total
      /* Owner call 2026-08-15: the percentage includes gift + free-shipping value
         (savings / originalTotal), flat format. Note: this basis reads higher than
         the PDP badge for the same tier — candidate for an A/B if ever questioned. */
      const pct = originalTotal > 0 ? Math.round((savings / originalTotal) * 100) : 0;
      els.savePct.textContent = 'SAVING ' + pct + '%';
      els.savePct.hidden = pct <= 0;
    }
    updateProgress(c);

    const hasSub = c.items.some(it => it.selling_plan_allocation && it.selling_plan_allocation.selling_plan && it.selling_plan_allocation.selling_plan.name);
    if (els.gifts) els.gifts.classList.toggle('is-active', !!(hasSub && hasGiftsConfigured));
    /* Collapsed summary carries the count, so the value still lands without the list open. */
    if (els.giftsCount) {
      const n = els.gifts ? els.gifts.querySelectorAll('.ncd__gift').length : 0;
      els.giftsCount.textContent = n ? n + ' FREE' : '';
    }
    updateRecs(c);
  }

  function buildItem(item, planName, compareUnit, effectiveLineTotal) {
    const unit = effectiveLineTotal / item.quantity;
    const compareHtml = compareUnit ? `<s class="ff-gtm n-f-14">${money(compareUnit)}</s>` : '';

    const parts = [];
    if (item.variant_title && item.variant_title !== 'Default Title') {
      parts.push(`<span class="ncd__item-variant ff-gtm n-f-14">${item.variant_title}</span>`);
    }
    if (planName) {
      parts.push(`<span class="ff-gtm n-f-13">${billingText(planName)}</span>`);
    }
    const subLine = parts.length
      ? `<div class="ncd__item-sub">${parts.join('<span class="ncd__item-dot"></span>')}</div>`
      : '';

    // Keep subscription packs as a single, fixed line while the team reviews
    // safeguards for unusually large recurring quantities.
    const qtyHtml = planName ? '' : `
        <div class="ncd__item-qty">
          <button type="button" class="ncd__qty-btn ncd__qty-minus" aria-label="Decrease quantity"${item.quantity <= 1 ? ' disabled' : ''}>
            <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 8h10" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
          </button>
          <span class="ncd__qty-val ff-gtmb n-f-16">${item.quantity}</span>
          <button type="button" class="ncd__qty-btn ncd__qty-plus" aria-label="Increase quantity">
            <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 3v10M3 8h10" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
          </button>
        </div>`;

    return `
    <div class="ncd__item" data-key="${item.key}">
      <img class="ncd__item-img" src="${item.image}&width=200" alt="">
      <div class="ncd__item-info">
        <p class="ncd__item-title ff-gtmb n-f-18">${item.product_title}</p>
        ${subLine}
        <div class="ncd__item-bottom">
          <div class="ncd__item-price ff-gtmb n-f-14">${money(unit)} ${compareHtml}</div>
          ${qtyHtml}
        </div>
      </div>
      <button class="ncd__item-delete" type="button" aria-label="Remove ${item.product_title}">
        <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9.375 3.125H15.625M3.125 6.25H21.875M19.7917 6.25L19.0612 17.2076C18.9516 18.8516 18.8968 19.6736 18.5417 20.2969C18.2291 20.8456 17.7575 21.2868 17.1892 21.5622C16.5437 21.875 15.7199 21.875 14.0722 21.875H10.9278C9.28011 21.875 8.45628 21.875 7.81077 21.5622C7.24246 21.2868 6.77093 20.8456 6.45832 20.2969C6.10324 19.6736 6.04844 18.8516 5.93884 17.2076L5.20833 6.25M10.4167 10.9375V16.1458M14.5833 10.9375V16.1458" stroke="currentColor" stroke-width="2.08333" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>`;
  }

  /* ── States ───────────────────────────────────────── */
  function setActiveState() {
    els.progress.style.display = 'flex';
    els.items.style.display = 'flex';
    els.footer.classList.add('is-active');
    els.empty.classList.remove('is-active');
  }

  function setEmptyState() {
    els.progress.style.display = 'none';
    els.items.style.display = 'none';
    els.footer.classList.remove('is-active');
    els.gifts.classList.remove('is-active');
    if (els.recs) els.recs.classList.remove('is-active');
    els.empty.classList.add('is-active');
  }

  function planNameOf(it) {
    return (it.selling_plan_allocation && it.selling_plan_allocation.selling_plan)
      ? it.selling_plan_allocation.selling_plan.name : null;
  }

  /* Is a subscription plan's interval longer than one month?
     "every 2 months" / "every year" → yes;  "every month" / weekly / one-time → no. */
  function subIsMultiMonth(planName) {
    if (!planName) return false;
    const s = planName.toLowerCase();
    if (/year/.test(s)) return true;
    const m = s.match(/(\d+)\s*month/);
    return m ? parseInt(m[1], 10) > 1 : false;
  }

  /* The plan's billing term as a shopper-facing phrase: "3 months", "6 months", "a year".
     Exists so the free-shipping strip can name the term the button will actually sign
     them up for. Hard-coding "3 months" in the setting was wrong the moment a 6-pack
     buyer saw it — the strip said 3, the button did 6. Empty for anything this can't
     read, and the caller leaves the copy alone rather than rendering a gap. */
  function planTerm(planName) {
    if (!planName) return '';
    const s = planName.toLowerCase();
    if (/year/.test(s)) return 'a year';
    const m = s.match(/(\d+)\s*month/);
    if (!m) return /month/.test(s) ? '1 month' : '';
    const n = parseInt(m[1], 10);
    return n > 1 ? n + ' months' : '1 month';
  }

  /* ── Progress bar ─────────────────────────────────── */
  /* Free shipping unlocks when ANY enabled rule passes:
       1. (optional) the cart has a subscription longer than 1 month;
       2. the cart has one of the picked "free shipping products" — and, if the
          "requires subscription" toggle is on, that product is subscribed (any plan);
       3. (default) the spend subtotal reaches the milestone threshold.
     Rule 3 is skipped whenever products are picked, matching the override behavior:
     in that case a locked cart hides the bar instead of showing a spend target. */
  /* Shared free-shipping qualification — drives BOTH the progress bar and the
     shipping value in the crossed-out subtotal/savings, so they always agree. */
  function freeShippingUnlocked(c) {
    const items = (c && c.items) || [];

    /* Rule 0 — the SKU mirror, and when it is set nothing else is consulted.
       Validated against every storefront order placed since 2026-07-29: 49 checkouts
       (app "Online Store" and "Shop"), 49 correct. Orders from TikTok Shop and from
       Skio renewals are created outside Shopify checkout, so the function never runs on
       them; the drawer never renders there either, so they are out of scope by
       construction rather than by exclusion.

       "Any single line item" in the function, so one qualifying line frees the whole
       order. Gifts and hidden SKUs are skipped the same way they are everywhere else. */
    if (freeShipSkus.length) {
      return items.some(function (it) {
        if (freeGifts.indexOf(it.variant_id) !== -1) return false;
        if (hiddenSKUs.indexOf(it.product_id) !== -1) return false;
        return skuQualifies(it.sku);
      });
    }

    // Rule 1 — any subscription longer than 1 month.
    if (FREE_SHIP_MULTIMONTH && items.some(function (it) { return subIsMultiMonth(planNameOf(it)); })) return true;

    // Rule 2 — picked products (optionally only when they're subscribed).
    const productMode = freeShipProducts.length > 0;
    if (productMode && items.some(function (it) {
      if (freeShipProducts.indexOf(it.product_id) === -1) return false;
      if (FREE_SHIP_SELECTED_REQ_SUB && !planNameOf(it)) return false;
      return true;
    })) return true;

    // Rule 3 — spend milestone (default; ignored while products are picked).
    if (!productMode && THRESHOLD > 0 && ((c && c.items_subtotal_price) || 0) >= THRESHOLD) return true;

    return false;
  }

  function updateProgress(c) {
    if (!els.progressFill || !els.progress) return;

    const unlocked = freeShippingUnlocked(c);
    const productMode = freeShipProducts.length > 0;
    /* The SKU mirror suppresses the spend milestone too. Without this, an owner who
       reads "overrides every setting above" and clears the product list would flip
       milestoneMode on — and rule 0 never looks at the subtotal, so the bar would fill
       to 100% and sit there reading "Spend $0.00 to unlock Free Shipping" forever. */
    const milestoneMode = !productMode && !freeShipSkus.length && THRESHOLD > 0;
    const shippingSubtotal = (c && c.items_subtotal_price) || 0;

    /* Cleared up front so the upsell state can't stick around after the cart changes
       (e.g. buyer upgrades to 3 months and the bar flips to "Unlocked"). The pending
       message goes with it: both branches below return early, so leaving it set would
       let injectSubBoxes repaint "upgrade for free shipping" over the top of the
       "You've Unlocked Free Shipping!" the buyer just earned by doing exactly that. */
    els.progress.classList.remove('is-upsell');
    pendingUpsellMsg = '';
    pendingUpsellHasSub = false;

    /* Goal node state. Set here, once, from the same boolean the message is chosen by,
       so the marker cannot disagree with the words above it — and set before the three
       returns below so every path is covered, including the upsell strip that
       injectSubBoxes paints later (that one only ever runs when !unlocked).
       This is the whole of the feature's logic: it needs no product, tier or SKU. */
    if (els.progressGoal) els.progressGoal.classList.toggle('is-earned', unlocked);

    if (unlocked) {
      els.progress.style.display = 'flex';
      els.progressFill.style.width = '100%';
      els.progress.style.setProperty('--ncd-progress', '100%');
      els.progressText.innerHTML = MSG_UNLOCKED;
      els.progressFill.classList.add('is-full');
      els.progressFill.classList.remove('is-glow');
      return;
    }

    if (milestoneMode) {
      els.progress.style.display = 'flex';
      const pct = Math.min((shippingSubtotal / THRESHOLD) * 100, 100);
      const remaining = (THRESHOLD - shippingSubtotal) / 100;
      els.progressFill.style.width = pct + '%';
      els.progress.style.setProperty('--ncd-progress', pct + '%');
      els.progressText.innerHTML = MSG_REMAINING.replace('[amount]', `<b>$${remaining.toFixed(2)}</b>`);
      els.progressFill.classList.remove('is-full');
      els.progressFill.classList.toggle('is-glow', pct > 0);
      return;
    }

    /* Products picked but nothing qualifies (or no milestone).
       Historically this just hid the bar, which left 1-bottle and one-time buyers with
       no indication that shipping isn't free until they reached checkout.

       We can't show "spend $X more" here: in productMode the spend milestone is never
       evaluated (see freeShippingUnlocked rule 3), so a dollar gap would be a promise
       the cart won't keep. The truthful next step is the plan upgrade, so that's what
       we offer.

       SUBSCRIBER carts (was IG test I6) ship this unconditionally as of 2026-07-30.
       It was never really a test: it is truthful messaging about an offer that
       already exists, filling a slot where the drawer showed nothing at all, and
       there is no outcome that would have made us keep the blank. Untested on
       purpose — two weeks of traffic to confirm what we would ship either way.

       ONE-TIME carts are decided in injectSubBoxes, where the offered tier is known,
       because "is this honest?" turns on what the button actually does:

         · 1 bottle one-time — control offers this variant's OWN monthly plan, which
           does not clear rule 1, so the strip would be a lie. It shows only under
           html.ig-i7, which repoints the block at a multi-month tier (getUpsellTarget)
           and makes the promise true. The two must switch together, and the swap trades
           RECURRING revenue ($32.95/mo, renewing) for immediate cash ($89.85 once) —
           a question for data, not judgement, hence the test.
         · 3-pack or 6-pack one-time — control ALREADY offers a plan billed every 3 or
           6 months, on this very line. The button earns free shipping in both arms, so
           the strip was being withheld for no reason. The ig-i7 gate was written for
           the 1-bottle case and over-applied here. This segment is identical in both
           arms (there is nothing to repoint), so saying so cannot bias the test.

       FAIL CLOSED. The strip is NOT painted here. updateProgress is synchronous and
       cannot know the product's ladder, so anything it paints is a guess — and the
       first cut guessed wrong in four ways, all customer-visible:
         · a 2-item cart got the message with no button anywhere (injectSubBoxes bails
           at effectiveCount >= 2, but the strip was already committed)
         · a multi-item cart decided "show" from ANY line having a plan, while the
           retraction judged it from the FIRST line's ladder — wrong in both directions
         · Super Focus saw a true-looking promise for a whole network round trip before
           the retraction pulled it, with a layout shift on the way out
         · a rate-limited ladder fetch left the promise standing with no way to act
       So the message is now only ever shown by injectSubBoxes, once it has the ladder
       in hand and is actually rendering the button that satisfies it. Stashed here;
       painted there. A brief absence is the right failure, a brief false promise is not. */
    const hasSub = (c && c.items || []).some(function (it) { return !!planNameOf(it); });
    pendingUpsellHasSub = hasSub;
    pendingUpsellMsg = hasSub ? MSG_UPSELL_SUB : MSG_UPSELL_OTP;

    els.progress.style.display = 'none';
  }

  /* Paint the upsell strip. Called only from injectSubBoxes, only once the offered tier
     is known to actually earn free shipping. */
  function showUpsellStrip(msg) {
    if (!els.progress || !els.progressFill || !els.progressText || !msg) return;
    els.progress.style.display = 'flex';
    els.progress.classList.add('is-upsell');
    /* Indicative fill, not a spend ratio — there is no numeric progress to show.
       It reads as "partway, one step to go".

       42, not the 33 it was: with a bare track the exact figure was arbitrary, but now
       that the track ends in a goal badge the fill is read against it, and a third felt
       stalled well short of the target. 42 is visibly past a third while staying clearly
       under half, so the badge still reads as something to reach rather than something
       already in hand. Anything from ~46 up starts reading as "halfway", which weakens
       the one-step-to-go message the copy is making. */
    els.progressFill.style.width = '42%';
    els.progress.style.setProperty('--ncd-progress', '42%');
    els.progressText.innerHTML = msg;
    els.progressFill.classList.remove('is-full');
    els.progressFill.classList.add('is-glow');
  }

  /* ── You May Also Like (recommendations) ──────────── */
  /* Shows the slider when the cart has items; hides any recommended product that's
     already in the cart. Cards are rendered once server-side — we only toggle visibility. */
  function updateRecs(c) {
    if (!els.recs || !hasRecs) return;
    if (effectiveCount(c) === 0) { els.recs.classList.remove('is-active'); return; }
    // Client direction (7/20 catchup): no "You May Also Like" once the cart holds 2+ items
    if (effectiveCount(c) >= 2) { els.recs.classList.remove('is-active'); return; }
    const inCart = {};
    (c.items || []).forEach(it => { inCart[it.product_id] = true; });
    let visible = 0;
    els.recs.querySelectorAll('.ncd__rec').forEach(card => {
      const pid = parseInt(card.getAttribute('data-rec-product'), 10);
      const hide = !!inCart[pid];
      card.hidden = hide;
      if (!hide) visible++;
    });
    els.recs.classList.toggle('is-active', visible > 0);
    requestAnimationFrame(updateRecArrows);
  }

  function scrollRecs(dir) {
    if (!els.recsTrack) return;
    const card = els.recsTrack.querySelector('.ncd__rec:not([hidden])');
    const step = card ? card.getBoundingClientRect().width + 15 : 220;
    els.recsTrack.scrollBy({ left: dir * step, behavior: 'smooth' });
  }

  /* Disable prev/next at the track ends (and when nothing overflows) so clicking
     past the first/last card is a clean no-op instead of scrolling into empty space. */
  function updateRecArrows() {
    if (!els.recsTrack || !els.recs) return;
    var t = els.recsTrack;
    var max = t.scrollWidth - t.clientWidth;
    var noScroll = max <= 1;
    var prev = els.recs.querySelector('.ncd__recs-prev');
    var next = els.recs.querySelector('.ncd__recs-next');
    if (prev) prev.disabled = noScroll || t.scrollLeft <= 1;
    if (next) next.disabled = noScroll || t.scrollLeft >= max - 1;
  }

  /* ADD from a recommendation — one-time line (no selling plan), then re-render. */
  async function addRec(variantId, btn) {
    /* The is-loading class is set HERE, after the guard — not by the click handler.
       It used to be set by the caller before this early return could fire, and
       `.ncd__rec-add.is-loading` is `opacity:.5; pointer-events:none`, so a dropped
       click left the button permanently dead: the recommendation cards are rendered
       once server-side and never re-rendered (see updateRecs), so nothing ever cleared
       it. The drop is easy to hit — openCart holds isUpdating for its whole sequence
       (/cart.js plus handleFreeGifts' add/update round trips) while the drawer is
       ALREADY on screen and the cards are already clickable, so anyone who opens the
       cart and goes straight for a recommendation lands in that window.
       performUpsell has always had this ordering right; this is now consistent with it. */
    if (isUpdating || !variantId) return;
    isUpdating = true;
    if (btn) btn.classList.add('is-loading');
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
      });
      if (!res.ok) throw new Error('Cart add failed: ' + res.status);
    } catch (e) {
      console.error('NCD rec add error:', e);
    } finally {
      isUpdating = false;
      /* Cleared on BOTH paths, not just the error path. The success path used to rely
         on the slider hiding itself (updateRecs drops it once the cart holds 2+ items),
         which masks the stuck button rather than clearing it — and stops masking it the
         moment that rule changes or the added line doesn't move effectiveCount.

         Cleared AFTER the refresh, not before: `is-loading` is pointer-events:none, so
         holding it for the duration of the re-render is what stops a second click from
         adding a second unit while /cart.js is still in flight. The old code got that
         protection by accident, from the same stuck class that was the bug — keep the
         protection, drop the accident.

         The inner try/finally is not redundant. refreshIfOpen -> openCart is throw-safe
         *today* because openCart wraps everything in its own try/catch — but a bare
         `await` here would silently resurrect the exact bug this fixes the moment that
         stops being true, and it would fail the same way: invisibly, on a button nobody
         re-renders. Guarantee the cleanup instead of inheriting it. */
      try {
        await refreshIfOpen();
      } finally {
        if (btn) btn.classList.remove('is-loading');
      }
    }
  }

  /* ── Free gifts (auto add/remove on subscription) ─── */

  /* The configured gifts this cart does not hold yet, as /cart/add.js items. Always
     qty 1: this used to read `(giftId === 50618185777466) ? 2 : 1`, an id that is not
     in the configured gift set and has not been for some time — so the branch could
     never fire. Owner confirmed 2026-08-01 that both gifts ship one each.
     Shared with performUpsell, so "which gifts are missing" is answered in one place. */
  function missingGiftItems(c) {
    if (!freeGifts.length || !c || !c.items) return [];
    const have = c.items.map(it => it.variant_id);
    return freeGifts
      .filter(giftId => have.indexOf(giftId) === -1)
      .map(giftId => ({ id: giftId, quantity: 1 }));
  }

  async function handleFreeGifts(currentCart) {
    if (!freeGifts.length) return null;

    const cartHasSub = currentCart.items.some(it => it.selling_plan_allocation && it.selling_plan_allocation.selling_plan && it.selling_plan_allocation.selling_plan.name);
    const giftsInCart = currentCart.items.filter(it => freeGifts.includes(it.variant_id));

    let updates = {};
    let itemsToAdd = [];
    let needsUpdate = false;

    if (cartHasSub) {
      /* Reconciliation only — the gifts a shopper earns by buying now travel with the
         purchase itself (theme-global.js addProduct, and performUpsell below), because
         Shopify lists cart lines newest first and anything added after the subscription
         sits above it at checkout. What is left for this path is the carts that reached
         a subscription some other way: a Replo lander, a restored cart, an admin edit.
         Those still get their gifts here, and still get them above the subscription —
         nothing in the Ajax API can move a line that is already in the cart. */
      itemsToAdd = missingGiftItems(currentCart);
      needsUpdate = itemsToAdd.length > 0;
    } else if (giftsInCart.length > 0) {
      giftsInCart.forEach(it => { updates[it.key] = 0; });
      needsUpdate = true;
    }

    if (!needsUpdate) return null;

    try {
      if (Object.keys(updates).length) {
        await fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates }) });
      }
      if (itemsToAdd.length) {
        await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: itemsToAdd }) });
      }
      return await fetch('/cart.js').then(r => r.json());
    } catch (e) {
      console.error('NCD free gift error:', e);
      return null;
    }
  }

  /* ── Item listeners (delete) ──────────────────────── */
  function attachItemListeners() {
    els.items.querySelectorAll('.ncd__item').forEach(el => {
      const key = el.getAttribute('data-key');
      const del = el.querySelector('.ncd__item-delete');
      if (del) del.addEventListener('click', () => removeItem(key, el));

      const minus = el.querySelector('.ncd__qty-minus');
      const plus = el.querySelector('.ncd__qty-plus');
      const valEl = el.querySelector('.ncd__qty-val');
      if (minus && plus && valEl) {
        const cur = parseInt(valEl.textContent, 10) || 1;
        minus.addEventListener('click', () => { if (cur > 1) changeQty(key, cur - 1); });
        plus.addEventListener('click', () => changeQty(key, cur + 1));
      }
    });
  }

  async function changeQty(key, qty) {
    if (isUpdating || qty < 1) return;
    isUpdating = true;
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: { [key]: qty } })
      });
      if (!res.ok) throw new Error('Cart update failed: ' + res.status);
    } catch (e) {
      console.error('NCD qty error:', e);
    } finally {
      isUpdating = false;
      await refreshIfOpen();
    }
  }

  async function removeItem(key, el) {
    if (isUpdating) return;
    el.classList.add('is-removing');
    isUpdating = true;
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: { [key]: 0 } })
      });
      if (!res.ok) throw new Error('Cart update failed: ' + res.status);
    } catch (e) {
      console.error('NCD remove error:', e);
      el.classList.remove('is-removing');
    } finally {
      isUpdating = false;
      await refreshIfOpen();
    }
  }

  /* ── Subscribe & Save upsell (subscription upgrade ladder) ──── */
  /* Per-product variant data (ordered, with sub price + hide_on_pdp) from the
     product.upsell.liquid template. /products/<handle>.js can't return metafields. */
  /* Memoise the in-flight PROMISE, not just the resolved value. A single "add to cart"
     produces several overlapping renders (add.js -> cart.js -> gift render -> reopen),
     and caching only the result meant each of them issued its own request for the same
     ladder — measured at 2-3 identical fetches per drawer open. This endpoint is
     aggressively rate-limited, and now that the free-shipping strip only paints when the
     ladder arrives, a 429 costs the buyer the upsell box AND the strip. Fewer requests,
     fewer 429s. Failures are not cached: the entry is dropped so the next open retries. */
  function getUpsellData(handle) {
    if (!handle) return Promise.resolve(null);
    if (productCache[handle]) return Promise.resolve(productCache[handle]);
    if (inflightUpsell[handle]) return inflightUpsell[handle];
    const req = fetch('/products/' + handle + '?view=upsell')
      .then(r => r.text())
      .then(t => { const d = JSON.parse(t); productCache[handle] = d; return d; })
      .catch(() => null)
      .then(d => { delete inflightUpsell[handle]; return d; });
    inflightUpsell[handle] = req;
    return req;
  }

  /* The next rung on the ladder for a cart line, or null when there's nothing to offer:
       one-time line         -> the SAME variant as a subscription (convert in place)
       subscription line     -> the next visible (non-hidden), available variant with a
                                plan (upgrade)
       top-tier subscription -> null (no box) */
  /* Ladder candidates above the cart line, smallest pack first: every variant with a larger
     custom.quantity (plus the line's own variant when includeSelf), ordered by quantity and
     then Admin position. Admin order alone breaks when a pack is added later (Lungs 2-pack
     sits after 5 and before 6), so a 2-pack would climb to 6 instead of 3. If any variant
     lacks a quantity, fall back to Admin order exactly as before. */
  function ladderAbove(variants, idx, includeSelf) {
    /* The upsell view defaults a missing quantity metafield to 1, so duplicate quantities
       mean "unknown": keep the old Admin order in that case. */
    const qtys = variants.map(v => v.qty);
    if (!qtys.every(q => q > 0) || new Set(qtys).size !== qtys.length) return variants.slice(includeSelf ? idx : idx + 1);
    const cur = variants[idx];
    return variants
      .map((v, i) => ({ v, i }))
      .filter(({ v, i }) => (includeSelf && i === idx) || v.qty > cur.qty)
      .sort((a, b) => a.v.qty - b.v.qty || a.i - b.i)
      .map(({ v }) => v);
  }

  async function getUpsellTarget(item) {
    const data = await getUpsellData(item.handle);
    if (!data || !data.variants) return null;
    const variants = data.variants;
    const idx = variants.findIndex(v => v.id === item.variant_id);
    if (idx === -1) return null;
    const isSub = !!(item.selling_plan_allocation && item.selling_plan_allocation.selling_plan);

    let target = null;
    if (!isSub) {
      /* IG test I7 — offer the first tier that ACTUALLY earns free shipping (a plan longer
         than a month) instead of this variant's own monthly plan.

         Control offers 1 bottle monthly, which does not qualify for free shipping. That
         made a free-shipping strip on a one-time cart a promise the only button on screen
         couldn't keep. Pointing the box at the multi-month tier lets the strip and the
         button finally name the same thing.

         Shares the ig-i7 gate with the one-time strip on purpose: the copy is only honest
         while this offer is in place, so they must switch together. The subscriber strip
         ships unconditionally (see updateProgress), so this bigger ask — $89.85/3mo vs
         $32.95/mo, on the coldest audience — is measured on its own rather than riding a
         messaging change we were going to make anyway. */
      if (document.documentElement.classList.contains('ig-i7')) {
        for (const v of ladderAbove(variants, idx, true)) {
          if (v.hidden || !v.available) continue;
          if (!v.sub_plan_id || v.sub_price == null) continue;
          /* Must be a tier that actually earns free shipping. In SKU mode that is the
             mirrored list; otherwise fall back to the old plan-interval test. */
          if (!tierEarnsFreeShipping(v.sku, v.plan_name)) continue;
          target = v;
          break;
        }
      }
      if (!target) {
        const cur = variants[idx];
        if (cur.sub_plan_id && cur.sub_price != null) target = cur;  // convert this variant to a sub
      }
    } else {
      for (const v of ladderAbove(variants, idx, false)) {          // climb to the next visible tier
        if (v.hidden || !v.available) continue;
        if (!v.sub_plan_id || v.sub_price == null) continue;
        target = v;
        break;
      }
    }
    if (!target) return null;

    const qty = item.quantity || 1;
    const lineSub = target.sub_price * qty;
    const lineReg = target.price * qty;
    // Per-unit ("per bottle/puck") savings = the tier's regular vs sub price divided by the
    // number of units in the pack (quantity metafield). unit_label comes from the metaobject.
    const perPack = target.qty && target.qty > 0 ? target.qty : 1;
    let unitSavings = Math.max(0, (target.price - target.sub_price) / perPack);
    /* Listed reference variants (e.g. the Lungs 2-pack) already sit below the single-bottle
       price, so "save vs one bottle" overstates the upgrade. For those lines only, compare
       the current pack's per-bottle subscription price with the next pack's. */
    const cur = variants[idx];
    if (isSub && target.id !== cur.id && referencePriceVariants.includes(Number(cur.id)) &&
        cur.sub_price != null && cur.qty > 0) {
      unitSavings = Math.max(0, cur.sub_price / cur.qty - target.sub_price / perPack);
    }
    const rawDiscountPct = lineReg > 0 ? (lineReg - lineSub) / lineReg * 100 : 0;
    return {
      variantId: target.id,
      sellingPlanId: target.sub_plan_id,
      qty: qty,
      label: target.label,
      planName: target.plan_name || '',
      sku: target.sku || '',
      unitLabel: target.unit_label || '',
      unitSavings: unitSavings,
      sameLine: target.id === item.variant_id,   // convert in place vs add-then-remove
      /* I7 only: a one-time buyer being offered a DIFFERENT (multi-month) tier. Needs its
         own copy — the upgrade set is written for people who already subscribed, so it
         never says "subscription" and the buyer can't tell they're starting one. */
      fromOneTime: !isSub && target.id !== item.variant_id,
      lineSub: lineSub,
      lineSavings: Math.max(0, lineReg - lineSub),
      discountPct: Math.round(rawDiscountPct)
    };
  }

  function fillTokens(tpl, alloc) {
    return String(tpl || '')
      .replace(/\[price\]/g, money(alloc.lineSub))
      .replace(/\[savings\]/g, money(alloc.lineSavings))
      .replace(/\[discount\]/g, alloc.discountPct + '%')
      .replace(/\[variant\]/g, alloc.label || '')
      .replace(/\[billing\]/g, alloc.planName ? billingText(alloc.planName) : '')
      .replace(/\[plan\]/g, alloc.planName || '')
      .replace(/\[unit_savings\]/g, money(alloc.unitSavings || 0))
      .replace(/\[unit\]/g, alloc.unitLabel || '')
      .replace(/\[term\]/g, planTerm(alloc.planName));
  }

  function buildSubBox(alloc) {
    const check = '<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M3 8.5L6.5 12L13 4.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    const perks = subBoxPerks.map(p => `<li class="ncd__subbox-perk ff-gtm n-f-13">${check}<span>${p}</span></li>`).join('');
    // Two copy sets: "subscribe" (converting a one-time line -> alloc.sameLine) and
    // "upgrade" (moving a subscription up to the next tier). Upgrade fields fall back to
    // the subscribe copy when left blank. [variant] resolves to the offered tier's name.
    const up = !alloc.sameLine;
    /* Three cases, not two:
         sameLine            convert this variant to its own subscription  -> "subscribe" copy
         up && fromOneTime   one-time buyer -> a multi-month tier (I7)     -> "start sub" copy
         up                  subscriber -> the next tier up               -> "upgrade" copy
       The third set exists because the upgrade wording assumes the reader already
       subscribed. Shown to a one-time buyer it never mentions a subscription at all, so
       they could commit to a recurring plan without being told. Each field falls back to
       the upgrade copy, then the subscribe copy, so blanks stay safe. */
    const otp = up && alloc.fromOneTime;
    const pick = (o, u, base) => (otp && o) ? o : ((up && u) ? u : base);
    const tag      = pick(subBoxCfg.tagOtp,      subBoxCfg.tagUp,      subBoxCfg.tag);
    const heading  = pick(subBoxCfg.headingOtp,  subBoxCfg.headingUp,  subBoxCfg.heading);
    /* Price is the one field that can't follow sameLine blindly. The subscribe template
       is "[price]/mo", which is only true of a monthly plan — and a one-time buyer of a
       3-pack or 6-pack converts IN PLACE to a plan billed every 3 or 6 months. That is
       sameLine, so it took the subscribe copy, and quoted "$89.85/mo" for $89.85 every
       three months. The upgrade template ("[billing]: [price]") names the interval, so
       use it whenever the offered plan is not monthly, however we got there. */
    const namesInterval = !alloc.sameLine || subIsMultiMonth(alloc.planName);
    const priceTpl = (namesInterval && subBoxCfg.priceTplUp) ? subBoxCfg.priceTplUp : subBoxCfg.priceTpl;
    const discTpl  = pick(null,                  subBoxCfg.discountUp, subBoxCfg.discountTpl);
    const button   = pick(subBoxCfg.buttonOtp,   subBoxCfg.buttonUp,   subBoxCfg.buttonOffer);
    return `
      <div class="ncd__subbox${up ? ' ncd__subbox--upgrade' : ''}">
        ${tag ? `<span class="ncd__subbox-tag n-font-w-700 n-f-12">${fillTokens(tag, alloc)}</span>` : ''}
        <div class="ncd__subbox-body">
          ${heading ? `<p class="ncd__subbox-heading n-font-w-700 n-f-16">${fillTokens(heading, alloc)}</p>` : ''}
          <div class="ncd__subbox-pricerow">
            <span class="ncd__subbox-price ff-gtm n-f-13">${fillTokens(priceTpl, alloc)}</span>
            ${discTpl ? `<span class="ncd__subbox-disc n-f-13 ff-pxeb">${fillTokens(discTpl, alloc)}</span>` : ''}
          </div>
          ${perks ? `<ul class="ncd__subbox-perks">${perks}</ul>` : ''}
          <button type="button" class="ncd__subbox-btn ff-icm n-f-18"><span class="n-btn__label">${fillTokens(button, alloc)}</span><span class="n-btn__shade n-btn__shade--left ncd__subbox-shade" aria-hidden="true"><svg width="82" height="59" viewBox="0 0 82 59" fill="none" style="overflow:hidden" xmlns="http://www.w3.org/2000/svg"> <g opacity="0.95"> <path d="M-6.44119 80.9688L-6.31417 70.5779L71.1141 0.858182L82.3849 0.984803L-6.44119 80.9688Z" fill="currentColor"/> </g> <path d="M64.6844 0.82753L-4.77667 62.5769L-6.19454 60.981L-5.89352 36.4526L34.6067 0.448594L64.6468 0.786069L64.6844 0.82753Z" fill="currentColor"/> </svg></span><span class="n-btn__shade n-btn__shade--right ncd__subbox-shade" aria-hidden="true"><svg width="88" height="60" viewBox="0 0 88 60" fill="none" style="overflow:hidden" xmlns="http://www.w3.org/2000/svg"> <g opacity="0.95"> <path d="M100.297 -25.9205L100.079 -15.5273L6.27532 67.5298L-4.99996 67.3137L100.297 -25.9205Z" fill="currentColor"/> </g> <g opacity="0.95"> <path d="M20.7339 63.0403L93.9357 -2.58579L93.5906 13.8413L28.7443 71.9757L20.7339 63.0403Z" fill="currentColor"/> </g> </svg></span></button>
        </div>
      </div>`;
  }

  async function injectSubBoxes(c, token) {
    if (!subBoxCfg.enable) return;
    /* Guard first, before the ladder fetch: an accepted upgrade means no box for the rest
       of this drawer session, so there is nothing to look up. This also keeps the box from
       flickering between the two renders performUpsell now does.

       Unconditional, and meant to stay that way. This was briefly gated behind `ig-i8` so
       the alternative could be A/B tested; the owner settled it instead on 2026-08-04 —
       re-offering the next rung to someone who just said yes is greedy, and that is a brand
       decision rather than a number to be discovered. Removing the gate rather than leaving
       it dormant is deliberate: a switch nobody remembers the reasoning for is a switch
       somebody eventually flips. The reasoning lives in the I8 row of the test log. */
    if (upsellTakenThisOpen) return;
    // Only the most recently added line gets an upsell box — not every line. Shopify lists
    // the newest item first, so the FIRST eligible line is the latest product.
    let item = null;
    for (const it of c.items) {
      if (hiddenSKUs.includes(it.product_id)) continue;
      if (freeGifts.includes(it.variant_id)) continue;
      item = it;
      break;
    }
    if (!item) return;

    // One-time line -> offer this variant as a sub; subscription line -> offer the next tier
    // up. getUpsellTarget returns null at the top of the ladder (then no box shows).
    const alloc = await getUpsellTarget(item);
    if (token !== renderToken) return;
    if (!alloc) return;
    // Client direction (7/20 catchup): drop the subscription upsell (both the
    // "Subscribe & Save" conversion box and the "Add More. Save More" upgrade box)
    // once the cart holds 2+ items.
    if (effectiveCount(c) >= 2) return;
    const itemEl = els.items.querySelector('.ncd__item[data-key="' + item.key + '"]');
    if (!itemEl) return;
    if (itemEl.nextElementSibling && itemEl.nextElementSibling.classList.contains('ncd__subbox')) return;
    itemEl.insertAdjacentHTML('afterend', buildSubBox(alloc));
    const box = itemEl.nextElementSibling;
    const btn = box && box.querySelector('.ncd__subbox-btn');
    if (btn) btn.addEventListener('click', () => performUpsell(item, alloc, btn));

    /* Only NOW is the free-shipping strip earned: we have a button on screen, and the
       tier behind it clears freeShippingUnlocked rule 1. Every earlier return in this
       function — no ladder, top of the ladder, 2+ items, no matching row — correctly
       leaves the strip hidden, so the promise and the means to act on it always ship
       together or not at all. Super Focus (all plans monthly) fails the check here.

       FREE_SHIP_MULTIMONTH is part of the test, not decoration: rule 1 only exists while
       that setting is on (see freeShippingUnlocked). It is on today, but it is a checkbox
       in the theme editor whose schema default is off — unticking it would otherwise turn
       this into "upgrade to 3 months for free shipping" on a store where 3 months no
       longer earns it. The strip must read the same switch the money reads.

       freeShippingUnlocked is belt-and-braces: updateProgress already clears the pending
       message on its unlocked path, and this re-asserts it locally so a future early
       return there can't quietly resurrect the bug.

       `honest` is the one-time question (see updateProgress). A subscriber always
       qualifies. A one-time buyer qualifies when the button converts THIS line to its
       own multi-month plan (sameLine — the 3-pack and 6-pack), or when ig-i7 has
       repointed the block at a tier that earns it. A 1-bottle one-time buyer in control
       is offered a monthly plan, is caught by subIsMultiMonth above, and correctly sees
       nothing.

       The copy runs through fillTokens so [term] names the term the button actually
       signs them up for — 6 for the 6-pack, not the "3 months" the setting hard-coded. */
    const honest = pendingUpsellHasSub || alloc.sameLine
      || document.documentElement.classList.contains('ig-i7');
    if (pendingUpsellMsg && honest && tierEarnsFreeShipping(alloc.sku, alloc.planName)
        && !freeShippingUnlocked(c)) {
      showUpsellStrip(fillTokens(pendingUpsellMsg, alloc));
    }
  }

  async function performUpsell(item, alloc, btn) {
    if (isUpdating) return;
    isUpdating = true;
    if (btn) btn.classList.add('is-loading');
    /* Hold checkout for the WHOLE upsell, armed before the first request rather than after
       the mutation. Arming it later — which is what this did first — leaves the mutation
       window itself unguarded: tap the upgrade, tap CHECKOUT 60ms later, and `giftsSettled`
       is still openCart's long-resolved promise, so the shopper navigates while the cart is
       mid-swap and loses the gift the upgrade was about to earn. Caught by driving exactly
       that sequence against a stubbed cart; it navigated at 67ms.
       The checkout handler races this against its own 1200ms cap, so an upsell that hangs
       can still never trap anyone on the button. */
    let settleUpsell;
    giftsSettled = new Promise(function (r) { settleUpsell = r; });
    try {
      if (alloc.sameLine) {
        // one-time -> same variant subscription: convert the existing line in place.
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: alloc.qty, selling_plan: alloc.sellingPlanId })
        });
        if (!res.ok) throw new Error('Cart change failed: ' + res.status);
        cart = await res.json();
      } else {
        /* Upgrade to the next variant on subscription: add the new line first, then remove
           the old one. These two calls are NOT atomic — if the remove fails after the add
           succeeds the shopper would be left with TWO subscriptions, so we roll the add back.

           Upgrading a one-time line can be the moment this cart first earns its gifts, so
           they are asked for in THIS request rather than left to handleFreeGifts below.
           Shopify lists cart lines newest first and adds `items` one at a time, so the
           array arrives reversed — send [gifts…, upgrade] and the upgraded subscription
           leads the cart and the checkout summary, with the ebooks under it. Same rule and
           same reason as theme-global.js addProduct. One request, so a gift can never land
           without the subscription that earns it. */
        const upgradeItem = { id: alloc.variantId, quantity: alloc.qty, selling_plan: alloc.sellingPlanId };
        const addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [upgradeItem].concat(missingGiftItems(cart)).reverse() })
        });
        if (!addRes.ok) throw new Error('Cart add failed: ' + addRes.status);
        const added = await addRes.json();
        /* Every line this request created, so the compensation below can undo all of it —
           the upgrade AND any gift that came with it, which would otherwise be left behind
           on a cart that is one-time again. `added.items` is the batch shape; the bare line
           object is what a single-item add returns, which is still what this sends once the
           gifts are already in the cart. */
        const addedLines = (added && added.items) || (added && added.key ? [added] : []);
        const remRes = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0 })
        });
        if (!remRes.ok) {
          // Compensate: remove the lines we just added so we never leave a duplicate sub.
          for (const line of addedLines) {
            if (!line || !line.key) continue;
            try {
              await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: line.key, quantity: 0 })
              });
            } catch (_) { /* reconciled in catch below */ }
          }
          throw new Error('Cart change failed: ' + remRes.status);
        }
        cart = await remRes.json();
      }
      /* Paint the upgraded cart NOW, before reconciling gifts. The mutation above already
         returned the authoritative cart, so the line, the total and the free-shipping strip
         are all knowable here — no reason to make the shopper watch a greyed-out button
         through the gift round trips as well. That wait was the whole of the delay:
         handleFreeGifts can fire /cart/update.js, /cart/add.js and a /cart.js re-read, so
         the old ordering held the paint behind up to five strictly serial requests
         (measured against production 2026-08-03: ~0.2-0.7s each from a datacenter, and
         mobile latency is a multiple of that). Now it is two.

         Two renders is safe here: both see the same upgraded cart so the strip resolves to
         the same message either way, upsellTakenThisOpen keeps the box from reappearing
         between them, and render() bumps renderToken so the first render's in-flight async
         work cancels itself rather than landing on top of the second. */
      upsellTakenThisOpen = true;
      render(cart);

      /* Subscribing/upgrading can unlock free gifts. Nothing to arm here — the hold set at
         the top of this function already covers these round trips, and settles in `finally`
         once they are done. Before that, `giftsSettled` was only ever assigned in openCart,
         so this whole path raced an already-resolved promise. */
      const updated = await handleFreeGifts(cart);
      if (updated) cart = updated;

      /* Shopify's cart mutation responses can arrive before an order-class discount
         (including a /discount/ link code) has been reallocated to the replacement line.
         Re-read the canonical cart once the upgrade and gift work is settled so the line
         price and footer never remain at the pre-code amount until the drawer is reopened. */
      try {
        cart = await fetch('/cart.js').then(function (r) {
          if (!r.ok) throw new Error('Cart refresh failed: ' + r.status);
          return r.json();
        });
      } catch (_) { /* keep the authoritative mutation response if reconciliation fails */ }
      render(cart);
    } catch (e) {
      console.error('NCD upsell error:', e);
      // Reconcile with the server so the drawer never shows a stale view that hides a
      // partial cart mutation (mirrors the openCart() re-sync the other mutators do).
      try {
        cart = await fetch('/cart.js').then(r => r.json());
        render(cart);
      } catch (_) { /* leave the current view; nothing better to show */ }
      if (btn) btn.classList.remove('is-loading');
    } finally {
      isUpdating = false;
      /* Always release, including on the error path — a failed upgrade must not leave
         checkout waiting on a promise that never settles. */
      settleUpsell();
    }
  }

  /* ── One-time listeners ───────────────────────────── */
  if (els.toggleDetails) {
    els.toggleDetails.addEventListener('click', () => {
      detailsOpen = !detailsOpen;
      els.toggleDetails.setAttribute('aria-expanded', detailsOpen ? 'true' : 'false');
      const label = els.toggleDetails.querySelector('.ncd__details-toggle-text');
      if (label) label.textContent = detailsOpen ? 'Hide Details' : 'Show Details';
    });
  }

  /* Free gifts collapse/expand. Collapsed by default so the pinned block costs ~44px
     instead of 207px; the summary row still carries the "unlocked" signal. */
  if (els.giftsToggle && els.giftsList) {
    els.giftsToggle.addEventListener('click', () => {
      const open = els.giftsList.hasAttribute('hidden');
      if (open) els.giftsList.removeAttribute('hidden');
      else els.giftsList.setAttribute('hidden', '');
      els.giftsToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  if (els.checkout) {
    /* Hold the navigation until the gifts are in, but only just. The drawer is on screen
       while handleFreeGifts is still in flight, so on mobile a quick tap used to reach
       checkout before the gift POST landed and the buyer lost what the PDP promised.
       Capped hard: a missed gift is bad, trapping someone on the checkout button is worse.
       Modified clicks (new tab, middle-click) are left entirely alone. */
    els.checkout.addEventListener('click', function (e) {
      /* The `loading` class goes on AFTER this guard, not before it. `.ncd__checkout.loading`
         is `opacity:.7; pointer-events:none`, and a modified click returns here without ever
         navigating — so setting it first left the ORIGINAL tab's checkout button permanently
         dead after a ⌘/ctrl-click opened checkout in a new one. Nothing re-renders it and no
         pageshow fires in that tab, so there was no recovery short of a reload. Same shape as
         the addRec bug fixed alongside this: a disabling class set before an early return. */
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      if (els.checkout.classList.contains('loading')) return;
      els.checkout.classList.add('loading');
      const href = els.checkout.getAttribute('href') || '/checkout';
      const capped = new Promise(function (r) { setTimeout(r, 1200); });
      const appReady = window.sbPrepareCheckout ? window.sbPrepareCheckout(els.checkout) : Promise.resolve(true);
      Promise.all([Promise.race([giftsSettled, capped]), appReady]).then(function (results) {
        if (results[1]) window.location.href = href;
        else els.checkout.classList.remove('loading');
      });
    });

    /* …and clear `loading` again on every pageshow.
       The class is deliberate — it stops a second tap double-submitting while the
       navigation to /checkouts/ is pending. Nothing removed it, which is fine for a
       normal navigation: the page unloads and the class goes with it.

       A back-forward-cache restore does NOT unload. Tap CHECKOUT, land on Shopify's
       checkout, hit Back, and the browser can restore this page with the DOM intact:
       drawer still open, checkout button still greyed out and unclickable, no way back
       short of a manual reload. Abandon-then-return is one of the most travelled paths
       in the funnel, iOS Safari is aggressive about bfcache, and 93% of this store's
       traffic is mobile. The deferred `window.location.href` above widens the window
       further — up to 1200ms where the class is set and the page has not gone anywhere.

       Whether a given page is bfcache-eligible depends on unload/beforeunload listeners
       from the theme and its apps, so this may already be moot on some pages — it was
       not verified in-browser (see docs/intelligems-test-log.md). Clearing on pageshow
       costs nothing when the class was never set, and the downside it guards against is
       a dead checkout button. */
    window.addEventListener('pageshow', () => els.checkout.classList.remove('loading'));
  }

  /* Recommendations: ADD buttons + prev/next arrows (cards are static, so wire once) */
  if (els.recs) {
    els.recs.querySelectorAll('.ncd__rec-add').forEach(btn => {
      btn.addEventListener('click', () => addRec(btn.getAttribute('data-rec-variant'), btn));
    });
    const rprev = els.recs.querySelector('.ncd__recs-prev');
    const rnext = els.recs.querySelector('.ncd__recs-next');
    if (rprev) rprev.addEventListener('click', () => scrollRecs(-1));
    if (rnext) rnext.addEventListener('click', () => scrollRecs(1));
    if (els.recsTrack) els.recsTrack.addEventListener('scroll', updateRecArrows, { passive: true });
    window.addEventListener('resize', updateRecArrows);

    /* Collapse toggle (mobile only via CSS): chevron or a tap anywhere on the
       header — except the prev/next arrows — opens/closes the slider. */
    const rtoggle = els.recs.querySelector('.ncd__recs-toggle');
    const rhead = els.recs.querySelector('.ncd__recs-head');
    function toggleRecs() {
      const open = els.recs.classList.toggle('is-open');
      if (rtoggle) rtoggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) requestAnimationFrame(updateRecArrows);
    }
    if (rtoggle) rtoggle.addEventListener('click', e => { e.stopPropagation(); toggleRecs(); });
    if (rhead) rhead.addEventListener('click', e => {
      if (e.target.closest('.ncd__recs-nav') || e.target.closest('.ncd__recs-toggle')) return;
      toggleRecs();
    });
  }

  document.querySelectorAll('.openCart').forEach(el => el.addEventListener('click', () => openCart()));
  document.querySelectorAll('.closeCart').forEach(el => el.addEventListener('click', closeCart));
  dialog.addEventListener('click', e => { if (e.target === dialog) closeCart(); });
  /* The native `close` event, not closeCart(), because ESC closes a <dialog> without going
     through our own close path — and a missed reset here would leave the upsell box
     suppressed for the rest of the visit rather than for the rest of the session. */
  dialog.addEventListener('close', () => {
    document.body.classList.remove('ncd-no-scroll');
    upsellTakenThisOpen = false;
  });

  if (typeof dialogPolyfill !== 'undefined') dialogPolyfill.registerDialog(dialog);

  /* ── Header count on load ─────────────────────────── */
  document.addEventListener('DOMContentLoaded', async () => {
    try {
      const initial = await fetch('/cart.js').then(r => r.json());
      cart = initial;
      const count = effectiveCount(initial);
      document.querySelectorAll('.headerCart').forEach(el => el.setAttribute('count', count));
      if (els.count) els.count.textContent = count;
    } catch (e) {
      console.error('NCD header count error:', e);
    }
  });

  // Expose the global API the rest of the theme relies on (product pages call openCart()
  // after add-to-cart). Keeps the new drawer a drop-in replacement for the legacy globals.
  window.openCart = openCart;
  window.closeCart = closeCart;
  window.ncdOpenCart = openCart;
  /* For pages that check out without ever opening the drawer — today that is /cart, whose
     button goes straight to /checkout. Fetches the cart, reconciles gifts, resolves. Never
     throws: a checkout must not be blocked by a gift. */
  window.ncdEnsureGifts = async function () {
    try {
      const fresh = await fetch('/cart.js').then(function (r) { return r.json(); });
      await handleFreeGifts(fresh);
    } catch (e) { /* leave the cart as-is rather than stop the buyer */ }
  };
  window.ncdCloseCart = closeCart;
})();
